/**
 * MentionChip: one client named on the weekly call, on the board under
 * the care manager's note. The client's name with the tag as a glyph and
 * a colour: gold with a hand and heart for On hospice, ink with a flower
 * for Passed away, the plain sand chip and no glyph for Other. The tag is
 * also in words for a screen reader and in the tooltip. One line: a long
 * name is cut with an ellipsis, never wrapped.
 */

import { type ReactNode } from 'react';
import { Flower2, HandHeart } from 'lucide-react';
import type { ClientCallMentionTag } from '@/lib/capacityApi';
import { MENTION_TAG_LABEL, MENTION_TAG_LOOK } from '@/lib/callMentions';

const GLYPH: Record<ClientCallMentionTag, typeof HandHeart | null> = {
  hospice: HandHeart,
  passed_away: Flower2,
  other: null,
};

export function MentionChip({ name, tag }: { name: string; tag: ClientCallMentionTag }): ReactNode {
  const Glyph = GLYPH[tag];
  return (
    <span
      data-testid="mention-chip"
      data-tag={tag}
      title={`${name} · ${MENTION_TAG_LABEL[tag]}`}
      className={`inline-flex max-w-[12rem] items-center gap-1 rounded-tight px-1.5 py-0.5 text-[0.6875rem] font-medium leading-4 ${MENTION_TAG_LOOK[tag]}`}
    >
      {Glyph && <Glyph aria-hidden="true" className="h-3 w-3 shrink-0" strokeWidth={2.25} />}
      <span className="truncate">{name}</span>
      {tag !== 'other' && <span className="sr-only">{`, ${MENTION_TAG_LABEL[tag]}`}</span>}
    </span>
  );
}
